/**
 * Josh P0-E multi-product stream ids + volume keys (DealPackage d0.2).
 * Canonical product names: oil, casinghead_gas, residue_gas.
 * Sedgwick also carries NGL via netNglBbl (legacy product id "ngl" / "gas").
 */

/** @type {ReadonlyArray<{ id: string, volKey: string, priceIndex: 'wti' | 'hh', aliases?: string[] }>} */
export const PRODUCT_STREAM_DEFS = Object.freeze([
  Object.freeze({ id: 'oil', volKey: 'netOilBbl', priceIndex: 'wti', aliases: ['crude'] }),
  Object.freeze({
    id: 'casinghead_gas',
    volKey: 'netMcf',
    priceIndex: 'hh',
    aliases: ['gas', 'casinghead'],
  }),
  Object.freeze({
    id: 'residue_gas',
    volKey: 'netResidueMcf',
    priceIndex: 'hh',
    aliases: ['residue'],
  }),
  Object.freeze({ id: 'ngl', volKey: 'netNglBbl', priceIndex: 'wti', aliases: ['ngl_bbl'] }),
]);

const ALIAS_TO_ID = (() => {
  const map = new Map();
  for (const def of PRODUCT_STREAM_DEFS) {
    map.set(def.id, def.id);
    for (const a of def.aliases || []) map.set(String(a).toLowerCase(), def.id);
  }
  return map;
})();

export function canonicalizeProductId(name) {
  if (name == null || name === '') return null;
  return ALIAS_TO_ID.get(String(name).toLowerCase()) || String(name).toLowerCase();
}

/** True when meta/registry marks gas-capable commodity (Eagle gas or oil+gas[+ngl]). */
export function packageHasGasProduct(metaOrDeal = {}) {
  const primary = metaOrDeal.primaryProduct || metaOrDeal.product;
  if (primary === 'gas') return true;
  const commodity = String(metaOrDeal.commodity || '').toLowerCase();
  if (commodity.includes('gas')) return true;
  const products = metaOrDeal.products;
  if (Array.isArray(products)) {
    return products.some((p) => {
      const id = canonicalizeProductId(p);
      return id === 'casinghead_gas' || id === 'residue_gas' || id === 'gas';
    });
  }
  return false;
}

/** Scan streams for positive volumes on any HH-priced product key. */
export function packageHasGasVolumes(pkg) {
  const gasKeys = PRODUCT_STREAM_DEFS.filter((d) => d.priceIndex === 'hh').map((d) => d.volKey);
  for (const stream of pkg?.streams || []) {
    for (const m of stream?.months || []) {
      for (const key of gasKeys) {
        const n = Number(m?.[key]);
        if (Number.isFinite(n) && n > 0) return true;
      }
    }
  }
  return false;
}

/**
 * Multi-product = oil-primary (or mixed) package that also carries gas volumes
 * and/or declares gas/casinghead/residue in meta.
 */
export function isMultiProductPackage(pkg) {
  if (!pkg) return false;
  const meta = pkg.meta || {};
  if (packageHasGasProduct(meta) && (meta.primaryProduct || meta.product) === 'oil') {
    return true;
  }
  const hasOil = (pkg.streams || []).some((s) =>
    (s.months || []).some((m) => (Number(m.netOilBbl) || 0) > 0),
  );
  return hasOil && packageHasGasVolumes(pkg);
}

/** Henry Hub default when an oil package has gas volume but no gas price. */
export const DEFAULT_HH = 3.42;

/** Gas is material when it is the primary product or at least 1% of BOE (6:1). */
export const GAS_MATERIAL_BOE_SHARE = 0.01;

export function packageVolumeTotals(pkg) {
  let oil = 0;
  let gas = 0;
  let ngl = 0;
  for (const stream of pkg?.streams || []) {
    for (const m of stream?.months || []) {
      oil += Number(m?.netOilBbl) || 0;
      gas += (Number(m?.netMcf) || 0) + (Number(m?.netResidueMcf) || 0);
      ngl += Number(m?.netNglBbl) || 0;
    }
  }
  return { oil, gas, ngl };
}

/** Gas BOE ÷ (oil + NGL + gas BOE). Zero when the package has no volume. */
export function packageGasBoeShare(pkg) {
  const { oil, gas, ngl } = packageVolumeTotals(pkg);
  const boe = oil + ngl + gas / 6;
  if (!(boe > 0)) return 0;
  return gas / 6 / boe;
}

export function gasIsMaterial(pkg) {
  const primary = pkg?.meta?.primaryProduct || pkg?.meta?.product || pkg?.assumptions?.primaryProduct;
  if (primary === 'gas') return true;
  return packageGasBoeShare(pkg) >= GAS_MATERIAL_BOE_SHARE;
}

/**
 * Gas price for an oil package that also has gas volumes.
 * Prefer an explicit gas base, then a Henry Hub-sized hh that is not a copy of WTI,
 * then the default HH. Never return a mirrored oil price.
 */
export function statedGasHh(pkg) {
  const meta = pkg?.meta || {};
  const assumptions = pkg?.assumptions || {};
  const wti = Number(meta.wtiBase ?? assumptions.wti);
  const candidates = [meta.gasHhBase, meta.hhBase, assumptions.hh, assumptions.hhBase];
  for (const c of candidates) {
    const n = Number(c);
    if (!Number.isFinite(n) || n < 0) continue;
    if (n < 15) return n;
    if (Number.isFinite(wti) && Math.abs(n - wti) < 1e-6) continue;
  }
  return DEFAULT_HH;
}

export function monthProductVolumes(month = {}) {
  const out = {};
  for (const def of PRODUCT_STREAM_DEFS) {
    const n = Number(month[def.volKey]);
    out[def.id] = Number.isFinite(n) ? n : 0;
  }
  return out;
}
